import { useEffect, useRef, useState, type RefObject } from 'react';
import { prefersReducedMotion } from './motion-prefs';

interface Flake {
  x: number;
  y: number;
  r: number;
  v: number;
  w: number;
  o: number;
}

function makeFlake(width: number, height: number, top?: boolean): Flake {
  return {
    x: Math.random() * width,
    y: top ? -10 : Math.random() * height,
    r: Math.random() * 2.6 + 0.8,
    v: Math.random() * 0.7 + 0.35,
    w: Math.random() * Math.PI * 2,
    o: Math.random() * 0.5 + 0.35,
  };
}

/** Canvas-снег (портировано из js/main.js, раздел «snow»). Переключатель — кнопка `#snowBtn` в шапке. */
export function useSnow(canvasRef: RefObject<HTMLCanvasElement | null>) {
  const [on, setOn] = useState(() => !prefersReducedMotion());
  // ref-копия состояния — чтобы цикл requestAnimationFrame видел актуальный on без пересоздания эффекта
  const onRef = useRef(on);
  onRef.current = on;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let width = 0;
    let height = 0;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let flakes: Flake[] = [];
    let mouseX = -999;
    let mouseY = -999;
    let raf = 0;

    const size = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    size();
    flakes = Array.from({ length: width < 600 ? 60 : 130 }, () => makeFlake(width, height));

    // Спрайт со свечением рисуется один раз (не на каждый кадр) — вместо shadowBlur на каждой частице каждый кадр.
    const baseR = 3;
    const spriteSize = 24;
    const spriteCenter = spriteSize / 2;
    const sprite = document.createElement('canvas');
    sprite.width = spriteSize;
    sprite.height = spriteSize;
    const spriteCtx = sprite.getContext('2d');
    if (spriteCtx) {
      spriteCtx.beginPath();
      spriteCtx.arc(spriteCenter, spriteCenter, baseR, 0, Math.PI * 2);
      spriteCtx.fillStyle = 'rgba(255,255,255,1)';
      spriteCtx.shadowColor = 'rgba(180,140,120,.35)';
      spriteCtx.shadowBlur = 3;
      spriteCtx.fill();
    }

    const onResize = () => size();
    const onMouseMove = (e: MouseEvent) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('mousemove', onMouseMove, { passive: true });

    const draw = () => {
      ctx.clearRect(0, 0, width, height);
      if (onRef.current) {
        for (const f of flakes) {
          f.w += 0.01;
          f.y += f.v;
          f.x += Math.sin(f.w) * 0.5;
          const dx = f.x - mouseX;
          const dy = f.y - mouseY;
          const d = dx * dx + dy * dy;
          if (d < 6400) {
            const k = ((6400 - d) / 6400) * 2;
            f.x += (dx / Math.sqrt(d + 1)) * k;
            f.y += (dy / Math.sqrt(d + 1)) * k;
          }
          if (f.y > height + 10) Object.assign(f, makeFlake(width, height, true));
          const scale = f.r / baseR;
          const size = spriteSize * scale;
          ctx.globalAlpha = f.o;
          ctx.drawImage(sprite, f.x - size / 2, f.y - size / 2, size, size);
          ctx.globalAlpha = 1;
        }
      }
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('mousemove', onMouseMove);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvasRef]);

  return { on, toggle: () => setOn((v) => !v) };
}
